import { useMemo, useRef } from 'react';

import type { Row } from '@tanstack/react-table';
import type {
  DataTableExternalSelectionAdapter,
  DataTableFeatures,
  DataTableSelectionSnapshot
} from 'design-system';

import { getSymbolsFromRows } from '../lib/getSymbolsFromRows';
import type { CommodityDataRow, CommodityTableRow } from './commoditiesTableColumns';
import useCommoditiesSelection from './useCommoditiesSelection';

import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../../types';

type CommodityReactTableRow = Row<DataTableFeatures, CommodityTableRow>;

function collectSelectableRows(
  rows: CommodityReactTableRow[],
  collected = new Map<string, CommodityDataRow>()
): CommodityDataRow[] {
  for (const row of rows) {
    if (row.original.kind === 'commodity') {
      if (!row.original.restricted) collected.set(row.original.id, row.original);
    } else {
      collectSelectableRows(row.subRows, collected);
    }
  }

  return Array.from(collected.values());
}

function getValues(rows: CommodityDataRow[]): CommoditiesTableRowType[] {
  return rows.map(row => row.value);
}

type UseCommoditiesSelectionAdapterOptions = {
  sections: CommoditiesTableSectionType[];
  selectionDisabled: boolean;
};

export function useCommoditiesSelectionAdapter({
  sections,
  selectionDisabled
}: UseCommoditiesSelectionAdapterOptions): DataTableExternalSelectionAdapter<CommodityTableRow> {
  const selection = useCommoditiesSelection(sections);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  return useMemo(() => {
    const getSnapshot = (rows: CommodityDataRow[]): DataTableSelectionSnapshot => {
      const symbols = getSymbolsFromRows(getValues(rows));
      const disabled = selectionDisabled || symbols.length === 0;
      const checked = !disabled && selectionRef.current.areAllSymbolsSelected(symbols);
      const hasSelection = !disabled && selectionRef.current.areAnySymbolsSelected(symbols);

      return {
        checked,
        disabled,
        indeterminate: hasSelection && !checked,
        locked: selectionRef.current.isSpotLocked && !disabled
      };
    };

    const toggle = (rows: CommodityDataRow[]) => {
      if (selectionDisabled || rows.length === 0) return;
      selectionRef.current.toggleSectionInSelection(getValues(rows));
    };

    return {
      getRowState: row => getSnapshot(collectSelectableRows([row])),
      getHeaderState: rows => getSnapshot(collectSelectableRows(rows)),
      toggleRow: row => toggle(collectSelectableRows([row])),
      toggleRows: rows => toggle(collectSelectableRows(rows))
    };
  }, [selectionDisabled]);
}
